import { Download, Eye, FileText, Paperclip, Upload, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { PdfPreviewDialog } from '@/components/PdfPreviewDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  DOCUMENT_STATUSES,
  DOCUMENT_STATUS_LABEL,
  TEMPLATE_TYPES,
  TEMPLATE_TYPE_LABEL,
  type DocumentStatus,
} from '../constants';
import type { Document } from '../types';
import { downloadDocumentFile, fetchDocumentFileBlob } from '../api';
import {
  useDeleteDocument,
  useEntityDocuments,
  useSetDocumentStatus,
  useSetDocumentType,
  useUploadDocument,
} from '../queries';

interface AttachmentsPanelProps {
  /** Django model label, e.g. "crm.deal". */
  modelLabel: string;
  objectId: number;
}

function isPdf(doc: Document): boolean {
  const target = doc.file ?? doc.name;
  return /\.pdf(\?|$)/i.test(target);
}

/** Color-coded pill styling per document status, so status is scannable. */
const STATUS_BADGE: Record<DocumentStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  rendered: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  pending_signature: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  signed: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  void: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
};

/** A compact, click-to-edit pill. Shows the current value; clicking opens a
 *  radio dropdown to change it — far lighter than an always-on <Select>. */
function EditableBadge<T extends string>({
  label,
  value,
  options,
  onChange,
  ariaLabel,
  className,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  ariaLabel: string;
  className?: string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className={cn(
            'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
        >
          {label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup value={value} onValueChange={(v) => onChange(v as T)}>
          {options.map((o) => (
            <DropdownMenuRadioItem key={o.value} value={o.value}>
              {o.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Files attached to a CRM entity. Reuses the generic Document model (generic
 *  FK), supports drag-drop upload and inline PDF preview. */
export function AttachmentsPanel({ modelLabel, objectId }: AttachmentsPanelProps) {
  const { contentTypeId, list, isPending, isError } = useEntityDocuments(modelLabel, objectId);
  const upload = useUploadDocument(modelLabel, objectId);
  const remove = useDeleteDocument(modelLabel, objectId);
  const setStatus = useSetDocumentStatus(modelLabel, objectId);
  const setType = useSetDocumentType(modelLabel, objectId);

  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<Document | null>(null);

  const previewId = preview?.id ?? null;
  // Auth-fetch the bytes via the API (JWT + CORS) — a bare iframe `src` to the
  // raw media URL 401s / fails CORS preflight.
  const fetchPreviewBlob = useCallback(
    () => fetchDocumentFileBlob(previewId as number),
    [previewId],
  );

  const docs = list.data ?? [];
  const canUpload = Boolean(contentTypeId) && !isError;

  async function handleFiles(files: FileList | null) {
    if (!files || !contentTypeId) return;
    for (const file of Array.from(files)) {
      await upload.mutateAsync({ file, name: file.name, content_type: contentTypeId });
    }
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Paperclip className="size-4" /> Attachments
        </CardTitle>
        <CardDescription>Files linked to this {modelLabel.split('.')[1]}.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <button
          type="button"
          disabled={!canUpload || upload.isPending}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={cn(
            'flex w-full flex-col items-center gap-1 rounded-md border border-dashed p-6 text-sm transition-colors',
            dragging ? 'border-primary bg-accent/40' : 'border-input hover:bg-accent/30',
            (!canUpload || upload.isPending) && 'cursor-not-allowed opacity-60',
          )}
        >
          <Upload className="text-muted-foreground size-5" />
          <span className="text-muted-foreground">
            {upload.isPending ? 'Uploading…' : 'Drag a file here, or click to upload'}
          </span>
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />

        {isError ? (
          <p className="text-destructive text-sm">Failed to load attachments.</p>
        ) : isPending ? (
          <div className="space-y-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : docs.length === 0 ? (
          <p className="text-muted-foreground text-sm">No attachments yet.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {docs.map((doc) => (
              <li key={doc.id} className="flex items-center gap-2 p-2 text-sm">
                <FileText className="text-muted-foreground size-4 shrink-0" aria-hidden />
                <span className="min-w-0 flex-1 truncate" title={doc.name}>
                  {doc.name}
                </span>
                <EditableBadge
                  ariaLabel={`Type of ${doc.name}`}
                  label={TEMPLATE_TYPE_LABEL[doc.type]}
                  value={doc.type}
                  options={TEMPLATE_TYPES}
                  onChange={(type) => setType.mutate({ id: doc.id, type })}
                  className="bg-muted text-muted-foreground"
                />
                <EditableBadge
                  ariaLabel={`Status of ${doc.name}`}
                  label={DOCUMENT_STATUS_LABEL[doc.status]}
                  value={doc.status}
                  options={DOCUMENT_STATUSES}
                  onChange={(status) => setStatus.mutate({ id: doc.id, status })}
                  className={STATUS_BADGE[doc.status]}
                />
                {isPdf(doc) && doc.file ? (
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Preview ${doc.name}`}
                    onClick={() => setPreview(doc)}
                  >
                    <Eye className="size-4" />
                  </Button>
                ) : null}
                {doc.file ? (
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Download ${doc.name}`}
                    onClick={() => void downloadDocumentFile(doc.id, doc.name)}
                  >
                    <Download className="size-4" />
                  </Button>
                ) : null}
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Remove ${doc.name}`}
                  onClick={() => remove.mutate(doc.id)}
                  disabled={remove.isPending}
                >
                  <X className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <PdfPreviewDialog
        open={Boolean(preview)}
        onOpenChange={(o) => !o && setPreview(null)}
        title={preview?.name ?? ''}
        fetchBlob={previewId ? fetchPreviewBlob : undefined}
      />
    </Card>
  );
}
